import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { ParamMap } from '@angular/router';
import { Observable } from 'rxjs';
import { environment } from '@environments/environment';
import {
  AuthProfileApiResponse,
  OAuthCallbackResult,
} from 'core/models/oneauth.model';
import { createOAuthState } from 'core/utils/oauth-state.util';
import { isProductAccessibleInJwt } from 'core/utils/jwt-claims.util';

const TOKEN_KEY = 'oneeduca_auth_token';
const REFRESH_TOKEN_KEY = 'oneeduca_auth_refresh_token';
const OAUTH_STATE_KEY = 'oneeduca_oauth_state';
const POST_LOGIN_REDIRECT_KEY = 'oneeduca_oauth_post_login_redirect';
/** Origem da app que iniciou o SSO local (cookie compartilhado entre portas). */
const LOOPBACK_HANDOFF_ORIGIN_KEY = 'oneeduca_oauth_loopback_handoff_origin';

@Injectable({ providedIn: 'root' })
export class OneAuthService {
  private readonly http = inject(HttpClient);

  redirectToLogin(intendedPath = '/', forcePrompt = false): void {
    if (typeof window === 'undefined') {
      return;
    }

    const state = createOAuthState();
    // localStorage + cookie: sessionStorage se perde no round-trip SSO
    localStorage.setItem(OAUTH_STATE_KEY, state);
    localStorage.setItem(POST_LOGIN_REDIRECT_KEY, intendedPath);
    sessionStorage.removeItem(OAUTH_STATE_KEY);
    sessionStorage.removeItem(POST_LOGIN_REDIRECT_KEY);
    this.writeOAuthStateCookie(state);

    const loginUrl = new URL(environment.authSpaUrl);
    loginUrl.searchParams.set('return_url', this.resolveAuthCallbackUrl());
    loginUrl.searchParams.set('state', state);

    if (forcePrompt) {
      loginUrl.searchParams.set('prompt', 'login');
    }

    window.location.replace(loginUrl.toString());
  }

  /**
   * No loopback, só :4206 está na allowlist do OneAuth DEV. Cantina (:4209) e
   * outras apps usam o callback do Admin como ponte e voltam com o token.
   * Retorna a origem de destino quando este callback é só a ponte.
   */
  consumeLoopbackHandoffOrigin(): string | null {
    if (typeof window === 'undefined') {
      return null;
    }

    const handoff = this.readCookie(LOOPBACK_HANDOFF_ORIGIN_KEY);
    if (!handoff) {
      return null;
    }

    if (window.location.origin === handoff) {
      this.clearCookie(LOOPBACK_HANDOFF_ORIGIN_KEY);
      return null;
    }

    this.clearCookie(LOOPBACK_HANDOFF_ORIGIN_KEY);
    return handoff;
  }

  forwardOAuthCallbackTo(origin: string): void {
    if (typeof window === 'undefined') {
      return;
    }

    const target = new URL('/auth/callback', origin);
    target.search = window.location.search;
    target.hash = window.location.hash;
    window.location.replace(target.toString());
  }

  handleOAuthCallback(params: ParamMap): OAuthCallbackResult {
    // Preferir a URL real: queryParamMap pode falhar com URLs muito longas (JWT).
    const fromWindow = this.readCallbackParamsFromWindow();
    const accessToken = fromWindow.accessToken ?? params.get('access_token');
    const refreshToken = fromWindow.refreshToken ?? params.get('refresh_token');
    const state = fromWindow.state ?? params.get('state');
    const expectedState =
      this.readOAuthState() ??
      localStorage.getItem(OAUTH_STATE_KEY) ??
      sessionStorage.getItem(OAUTH_STATE_KEY);

    if (!accessToken) {
      return { success: false, error: 'Token de acesso não recebido.' };
    }

    // State ausente = cache/site data limpo no meio do SSO. O token já veio
    // no redirect de 1ª parte; recusar aqui impede o login após limpar cache.
    if (expectedState && (!state || state !== expectedState)) {
      this.clearTokens();
      this.clearOAuthPending();
      return {
        success: false,
        error:
          'Não foi possível confirmar o login com segurança. Feche esta aba e tente entrar novamente.',
      };
    }

    this.clearOAuthPending();
    this.persistTokens(accessToken, refreshToken);
    return { success: true };
  }

  consumePostLoginRedirect(): string {
    const redirect =
      localStorage.getItem(POST_LOGIN_REDIRECT_KEY) ??
      sessionStorage.getItem(POST_LOGIN_REDIRECT_KEY) ??
      '/';
    localStorage.removeItem(POST_LOGIN_REDIRECT_KEY);
    sessionStorage.removeItem(POST_LOGIN_REDIRECT_KEY);
    return redirect.startsWith('/') ? redirect : '/';
  }

  getToken(): string | null {
    return this.readStoredValue(TOKEN_KEY);
  }

  getRefreshToken(): string | null {
    return this.readStoredValue(REFRESH_TOKEN_KEY);
  }

  isProductAccessible(token?: string | null): boolean {
    const value = token ?? this.getToken();

    if (!value) {
      return false;
    }

    return isProductAccessibleInJwt(value);
  }

  fetchProfile(accessToken?: string): Observable<AuthProfileApiResponse> {
    return this.http.get<AuthProfileApiResponse>(
      `${environment.apiUrl}/auth/me`,
      { headers: this.bearerHeaders(accessToken) },
    );
  }

  recordLoginSession(
    accessToken?: string,
  ): Observable<{ ok: boolean; message: string }> {
    return this.http.post<{ ok: boolean; message: string }>(
      `${environment.apiUrl}/auth/login`,
      {},
      { headers: this.bearerHeaders(accessToken) },
    );
  }

  async logoutAndRedirectToSso(): Promise<void> {
    if (typeof window === 'undefined') {
      return;
    }

    const refreshToken = this.getRefreshToken();
    const accessToken = this.getToken();

    // Dispara limpeza em background (keepalive) e redireciona na hora,
    // para não piscar o dashboard sem sessão.
    this.fireAndForgetLogoutCleanup(accessToken, refreshToken);

    this.clearTokens();
    this.clearOAuthPending();
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem('sge.admin.auth.session');
    }

    const returnUrl = this.resolvePostLogoutReturnUrl();
    window.location.replace(this.buildSsoLogoutUrl(returnUrl));
  }

  private fireAndForgetLogoutCleanup(
    accessToken: string | null,
    refreshToken: string | null,
  ): void {
    if (accessToken) {
      void fetch(`${environment.apiUrl}/auth/logout`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ refresh_token: refreshToken }),
        keepalive: true,
      }).catch(() => undefined);
    }

    if (refreshToken) {
      const body = new URLSearchParams({
        client_id: environment.oauth.clientId,
        token: refreshToken,
        token_type_hint: 'refresh_token',
      });

      void fetch(`${environment.authBaseUrl}/v1/oauth/token/revoke`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: body.toString(),
        keepalive: true,
      }).catch(() => undefined);
    }
  }

  clearTokens(): void {
    this.removeStoredValue(TOKEN_KEY);
    this.removeStoredValue(REFRESH_TOKEN_KEY);
  }

  clearOAuthPending(): void {
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem(OAUTH_STATE_KEY);
      localStorage.removeItem(POST_LOGIN_REDIRECT_KEY);
    }
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.removeItem(OAUTH_STATE_KEY);
      sessionStorage.removeItem(POST_LOGIN_REDIRECT_KEY);
    }
    this.clearOAuthStateCookie();
    this.clearCookie(LOOPBACK_HANDOFF_ORIGIN_KEY);
  }

  private writeOAuthStateCookie(state: string): void {
    this.writeCookie(OAUTH_STATE_KEY, state, 600);
  }

  private readOAuthState(): string | null {
    return this.readCookie(OAUTH_STATE_KEY);
  }

  private clearOAuthStateCookie(): void {
    this.clearCookie(OAUTH_STATE_KEY);
  }

  private writeCookie(
    name: string,
    value: string,
    maxAgeSeconds: number,
  ): void {
    if (typeof document === 'undefined') {
      return;
    }

    const secure = window.location.protocol === 'https:' ? '; Secure' : '';
    document.cookie = `${name}=${encodeURIComponent(value)}; Path=/; Max-Age=${maxAgeSeconds}; SameSite=Lax${secure}`;
  }

  private readCookie(name: string): string | null {
    if (typeof document === 'undefined') {
      return null;
    }

    const prefix = `${name}=`;
    const match = document.cookie
      .split(';')
      .map((part) => part.trim())
      .find((part) => part.startsWith(prefix));

    if (!match) {
      return null;
    }

    return decodeURIComponent(match.slice(prefix.length));
  }

  private clearCookie(name: string): void {
    if (typeof document === 'undefined') {
      return;
    }

    const secure = window.location.protocol === 'https:' ? '; Secure' : '';
    document.cookie = `${name}=; Path=/; Max-Age=0; SameSite=Lax${secure}`;
  }

  private persistTokens(
    accessToken: string,
    refreshToken: string | null,
  ): void {
    this.writeStoredValue(TOKEN_KEY, accessToken);

    if (refreshToken) {
      this.writeStoredValue(REFRESH_TOKEN_KEY, refreshToken);
    } else {
      this.removeStoredValue(REFRESH_TOKEN_KEY);
    }
  }

  private bearerHeaders(accessToken?: string | null): Record<string, string> {
    const token = accessToken ?? this.getToken();
    const headers: Record<string, string> = { Accept: 'application/json' };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    return headers;
  }

  private readStoredValue(key: string): string | null {
    if (typeof sessionStorage !== 'undefined') {
      const fromSession = sessionStorage.getItem(key);
      if (fromSession) {
        return fromSession;
      }
    }

    if (typeof localStorage === 'undefined') {
      return null;
    }

    const fromLocal = localStorage.getItem(key);
    if (fromLocal && typeof sessionStorage !== 'undefined') {
      sessionStorage.setItem(key, fromLocal);
    }

    return fromLocal;
  }

  private writeStoredValue(key: string, value: string): void {
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.setItem(key, value);
    }
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(key, value);
    }
  }

  private removeStoredValue(key: string): void {
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.removeItem(key);
    }
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem(key);
    }
  }

  private buildSsoLogoutUrl(returnUrl: string): string {
    const logoutUrl = new URL(`${environment.authBaseUrl}/sso/logout`);
    logoutUrl.searchParams.set('return_url', returnUrl);
    return logoutUrl.toString();
  }

  private resolveAuthCallbackUrl(): string {
    if (typeof window === 'undefined') {
      return environment.authCallbackUrl;
    }

    const origin = window.location.origin;
    if (this.isLoopbackOrigin(origin)) {
      const canonical = this.canonicalLocalCallbackUrl();
      let canonicalOrigin = '';
      try {
        canonicalOrigin = new URL(canonical).origin;
      } catch {
        canonicalOrigin = '';
      }

      // OneAuth DEV rejeita return_url fora da allowlist (ex.: :4209) e cai no
      // DenunciaNR1. Ponte via callback canônico (:4206) + cookie de handoff.
      if (canonicalOrigin && origin !== canonicalOrigin) {
        this.writeCookie(LOOPBACK_HANDOFF_ORIGIN_KEY, origin, 600);
        return canonical;
      }

      this.clearCookie(LOOPBACK_HANDOFF_ORIGIN_KEY);
      return canonical || `${origin}/auth/callback`;
    }

    this.clearCookie(LOOPBACK_HANDOFF_ORIGIN_KEY);
    return `${origin}/auth/callback`;
  }

  private canonicalLocalCallbackUrl(): string {
    return environment.authCallbackUrl;
  }

  private resolvePostLogoutReturnUrl(): string {
    const callbackUrl = this.resolveAuthCallbackUrl();
    const appLoginUrl =
      callbackUrl.replace(/\/auth\/callback\/?$/, '/login') + '?reauth=1';

    if (typeof window === 'undefined') {
      return appLoginUrl;
    }

    if (!this.isLoopbackOrigin(window.location.origin)) {
      return `${window.location.origin}/login?reauth=1`;
    }

    // /sso/logout no OneAuth DEV não aceita localhost:4206. Sem return_url
    // válido o SSO cai no fallback DenunciaNR1. A SPA Auth está na allowlist
    // - usamos ela como bounce de volta ao callback local.
    const state = createOAuthState();
    localStorage.setItem(OAUTH_STATE_KEY, state);
    localStorage.setItem(POST_LOGIN_REDIRECT_KEY, '/');
    this.writeOAuthStateCookie(state);

    const authLogin = new URL(environment.authSpaUrl);
    authLogin.searchParams.set('return_url', callbackUrl);
    authLogin.searchParams.set('state', state);
    authLogin.searchParams.set('prompt', 'login');
    return authLogin.toString();
  }

  private isLoopbackOrigin(origin: string): boolean {
    try {
      const hostname = new URL(origin).hostname;
      return hostname === 'localhost' || hostname === '127.0.0.1';
    } catch {
      return false;
    }
  }

  private readCallbackParamsFromWindow(): {
    accessToken: string | null;
    refreshToken: string | null;
    state: string | null;
  } {
    if (typeof window === 'undefined') {
      return { accessToken: null, refreshToken: null, state: null };
    }

    const query = new URLSearchParams(window.location.search);
    const hash = new URLSearchParams(window.location.hash.replace(/^#/, ''));

    return {
      accessToken: query.get('access_token') ?? hash.get('access_token'),
      refreshToken: query.get('refresh_token') ?? hash.get('refresh_token'),
      state: query.get('state') ?? hash.get('state'),
    };
  }
}
